import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Pill, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Account created!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAFAFC]">
      <div className="flex items-center justify-center p-6 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-teal-700 text-white"><Pill className="h-5 w-5" /></span>
            <span className="font-bold text-lg" style={{ fontFamily: "Outfit, sans-serif" }}>EZ Education</span>
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Create account</h1>
          <p className="text-sm text-slate-500 mb-6">Start learning for free today.</p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5" placeholder="Jane Pharma" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="register-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" placeholder="you@example.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="register-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1.5" placeholder="Min 6 characters" />
            </div>
            <Button data-testid="register-submit-button" type="submit" disabled={loading} className="w-full bg-teal-700 hover:bg-teal-600 text-white h-11">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
            </Button>
          </div>
          <p className="text-sm text-slate-500 mt-6 text-center">
            Already have an account? <Link to="/login" className="text-teal-700 font-semibold">Sign in</Link>
          </p>
        </form>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src="https://images.unsplash.com/photo-1601097874965-f940d4f012b5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="Study" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-teal-900/60" />
        <div className="absolute bottom-10 left-10 text-white max-w-sm">
          <Pill className="h-8 w-8 mb-4" />
          <h2 className="text-3xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Join EZ Education.</h2>
          <p className="mt-3 text-white/80">Access free courses instantly, upgrade anytime via UPI.</p>
        </div>
      </div>
    </div>
  );
}
